import React, { useEffect } from 'react';
import timer from '../../../assets/path.png';
import dr15_2 from "../../../assets/dr15_2.png";
import character from "../../../assets/exam_character.png";

const StepThreeContent = ({
  nextStep,
  prevStep,
  questions,
  selectedAnswers,
  handleAnswerSelect,
  handleTimeReamain,
  assessmentInfo,
  loading,
}: any) => {
  const [activeQIdx, setActiveQIdx] = React.useState(0);
  const [maxIndex, setMaxIndex] = React.useState(-1);
  const [questionStatus, setQuestionStatus] = React.useState(
    Array(questions.length).fill('default')
  );
  const [maxVisitedIndex, setMaxVisitedIndex] = React.useState(0);
  const [time, setTime] = React.useState(assessmentInfo?.duration || 0);
  const [displayTime, setDisplayTime] = React.useState("");

  const nextQ = () => {
    setActiveQIdx((prevIdx) => ++prevIdx);
    setMaxVisitedIndex(maxVisitedIndex + 1);
  };
  const prevQ = () => {
    setActiveQIdx((prevIdx) => --prevIdx);
  };

  const getQuestionNumberStyle = (questionIdx: any) => {
    const status = questionStatus[questionIdx];
    const isActive = questionIdx === activeQIdx;

    if (isActive) {
      return 'bg-amber-300 border-amber-300';
    } else {
      switch (status) {
        case 'default':
          return 'border-light-black';
        case 'answered':
          return 'bg-green-500 text-white border-green-500';
        case 'unanswered':
          return 'border-red-500 text-red-500';
        default:
          return 'border-light-black';
      }
    }
  };

  React.useEffect(() => {
    const initialStatus = questions.map((question: any, index: any) => {
      if (selectedAnswers[index]) {
        return 'answered';
      } else {
        return 'default';
      }
    });

    setQuestionStatus(initialStatus);

    const unansweredQuestionIndices: number[] = [];

    for (let i = 0; i <= Math.max(activeQIdx, maxIndex); i++) {
      if (!selectedAnswers[i]) {
        unansweredQuestionIndices.push(i);
      }
    }

    setMaxIndex(Math.max(maxIndex, activeQIdx));

    const updatedStatus = initialStatus.map((status: string, index: number) => {
      if (unansweredQuestionIndices.includes(index)) {
        return 'unanswered';
      }
      return status;
    });

    setQuestionStatus(updatedStatus);

    // console.log('Unanswered Question Indices:', unansweredQuestionIndices);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questions, selectedAnswers, activeQIdx]);

  useEffect(() => {
    const hours = Math.floor(time / 3600);
    const minutes = Math.floor((time % 3600) / 60);
    const seconds = time % 60;

    const formattedTime =
      hours > 0
        ? `${hours}h ${String(minutes).padStart(2, '0')}m ${String(seconds).padStart(2, '0')}s`
        : `${String(minutes).padStart(2, '0')}m ${String(seconds).padStart(2, '0')}s`;

    setDisplayTime(formattedTime);
    handleTimeReamain(formattedTime);
  }, [time, handleTimeReamain]);

  useEffect(() => {
    if (time > 0) {
      const timerId = setInterval(() => {
        setTime((prevTime: any) => Math.max(prevTime - 1, 0));
      }, 1000);

      return () => clearInterval(timerId);
    }
  }, [time]);
  const styles = {
    bottomBg: {
      position: "fixed" as const,
      top: "10%",
      left: 0,
      bottom: 0,
      width: "100%",
      height: "auto",
      backgroundSize: "contain",
      backgroundRepeat: "no-repeat",
      backgroundPosition: "bottom center",
      backgroundImage: `url(${dr15_2})`,
    },
    plant: {
      position: "fixed" as const,
      bottom: "0",
      right: "0",
      width: "20rem",
      height: "auto",
      maxWidth: "100%",
      pointerEvents: "none" as const,
    }
  }
  return (
    <>
      <section
        className="md:py-7 py-5 bg-transparent min-h-[calc(100vh-152px)] mb-[10rem]"
        style={{ position: "relative" }}
      >
        <div className="mx-auto max-w-7xl px-4">
          <div className="flex justify-between flex-wrap">
            {/* Left Section (Answers & Timer) */}
            <div className="lg:w-3/12 w-full lg:order-2 flex flex-col items-center">
              {/* Timer Box */}
              <div className=" flex justify-center items-center bg-white-900 text-center p-3 rounded-lg w-full max-w-xs ">
                <h4 className="text-white flex items-center justify-center md:text-sm text-xs mb-1">
                  <img className="md:w-6 w-5 md:h-6 h-5 mr-1" src={timer} alt="" />
                </h4>
                <h3 className="font-semibold sm:text-base md:text-sm text-xs text-gray" style={{ fontFamily: '"Inter", sans-serif', fontWeight: "600", fontSize: "20px" }}>
                  {displayTime}
                </h3>
              </div>

              {/* Answer Status Box */}
              <div
                className="sm:p-5 p-4 rounded-md bg-white border-2 w-full max-w-xs"
                style={{
                  boxShadow: "0px 2px 6px gray",
                  maxHeight: "500px",  // Adjust height as needed
                  overflowY: "auto",   // Enables vertical scrolling
                }}
              >
                <p
                  className="md:text-base text-sm font-medium mb-4"
                  style={{ fontFamily: '"Inter", sans-serif' }}
                >
                  Answer Status
                </p>
                <div className="grid grid-cols-4 gap-2">
                  {questions?.map((question: any, questionIdx: number) => (
                    <div
                      key={questionIdx}
                      className={`inline-block h-10 w-10 md:leading-10 leading-10 rounded-lg text-center border-2 md:text-base text-sm font-medium ${getQuestionNumberStyle(
                        questionIdx
                      )}`}
                      onClick={(event) => {
                        event.preventDefault();
                        setActiveQIdx(questionIdx);
                      }}
                      style={{ fontFamily: '"Inter", sans-serif' }}
                    >
                      {questionIdx + 1}
                    </div>
                  ))}
                </div>
              </div>

            </div>

            {/* Right Section (Question & Answers) */}
            <div className="lg:w-8/12 w-full lg:order-1">
              <h3 className="font-bold text-xl my-3" style={{ fontFamily: '"Inter", sans-serif' }}>Question {activeQIdx + 1}</h3>
              <div className="md:mb-8 mb-6">
                <h2 className="md:text-base text-sm font-medium mb-8" style={{ fontSize: "22px", fontFamily: '"Inter", sans-serif' }}>
                  {questions && questions[activeQIdx]?.title}
                </h2>
                <ul>
                  {questions &&
                    questions[activeQIdx]?.answers?.map((answer: any, idx: number) => (
                      <li key={idx} style={{ fontFamily: '"Inter", sans-serif', fontSize: "19px" }}>
                        <input
                          type="checkbox"
                          name={`question_${activeQIdx}`}
                          checked={selectedAnswers[activeQIdx]?.some(
                            (selectedAnswer: any) => selectedAnswer.answerId === answer._id
                          )}
                          onChange={() => handleAnswerSelect(activeQIdx, idx)}
                          className="answer-checkbox mx-6 my-2 mb-[2rem]"
                          style={{
                            width: "20px",  // Increase checkbox size
                            height: "20px", // Increase checkbox size
                            border: "2px solid yellow",  // ✅ Correct Syntax
                            borderRadius: "4px" // Change border color (modern browsers)
                          }}
                        />
                        {answer?.title}
                      </li>
                    ))}
                </ul>
              </div>
              <div className="md:mt-10 mt-7">
                {activeQIdx !== 0 && (
                  <div
                    onClick={prevQ}
                    className="cursor-pointer inline-block md:py-4 py-3 md:px-10 px-6 rounded-lg bg-shade-gray font-medium md:text-base text-sm"
                  >
                    Previous
                  </div>
                )}
                <div
                  onClick={activeQIdx === questions.length - 1 ? nextStep : nextQ}
                  className="cursor-pointer inline-block md:py-4 py-3 md:px-10 px-6 rounded-lg bg-amber-300 font-medium md:text-base text-sm ml-6"
                >
                  {activeQIdx === questions.length - 1 ? "Submit" : "Next"}
                </div>
              </div>
            </div>
          </div>
        </div>
        <img
          src={character}
          alt="Bottom Right Image"
          style={{ ...styles.plant, zIndex: -2 }}
        />
        <div style={{ ...styles.bottomBg, zIndex: -4 }}></div>
      </section>
    </>

  );
};

export default StepThreeContent;
